import * as R from 'remeda';
import {
  AnyOpportunityDetectionSchema,
  CompetitorLossAssessment,
  HandoffFailureAssessment,
  LeadAbandonmentAssessment,
  OpportunityVerificationPromptType,
  PhoneScreenFollowUpAssessment,
  PlacementFrustrationAssessment,
  PlacementRiskAssessment,
  QuestionDetectionResult,
  ResponseTimeAssessment,
  WorkersCompLegalRiskAssessment,
  InboundFollowUpAssessment,
} from './schemas/llmResponseSchemas.js';
import { z } from 'zod';
import {
  DemoOpportunityType,
  DurationLikeObject,
  OpportunityType,
  OpportunityStatus,
  realAndDemoOpportunityTypes,
  realOpportunityTypes,
  RealOrDemoOpportunityType,
} from './schemas/common.js';
import { has } from './utils.js';
import { AccessLevel, doesUserHaveAccess } from './permissions.js';
import { OpportunityTypeConfigs } from './schemas/models/index.js';

export {
  OpportunityType,
  DemoOpportunityType,
  realAndDemoOpportunityTypes,
  realOpportunityTypes,
} from './schemas/common.js';

// Default throttle for auto-resolve attempts (in hours)
export const DEFAULT_AUTO_RESOLVE_THROTTLE_HOURS = 24;

// Icon mappings for unimplemented opportunity types
const demoOpportunityTypeIcons = {
  placementGhostingRisk: 'ghost',
  submissionBackOutRisk: 'arrow-right-to-bracket',
  submissionGhostingRisk: 'ghost',
  callOffEscalation: 'phone-slash',
  facilityCanceledShift: 'rectangle-xmark',
  redeploymentRisk: 'person-circle-exclamation',
  redeploymentRiskNotEngaged: 'person-circle-minus',
  outreachResponseTrendUp: 'chart-line',
  outreachResponseTrendDown: 'chart-gantt',
  engagementAttemptTrendUp: 'chart-line',
  engagementAttemptTrendDown: 'chart-gantt',
  clientRequestReminder: 'clock',
  clientVisitNoteQualityTrendUp: 'arrow-trend-up',
  clientVisitNoteQualityTrendDown: 'arrow-trend-down',
  clientVisitWithMinimalData: 'circle-exclamation',
  competitorThemeTrendUp: 'lightbulb',
} as const satisfies Record<DemoOpportunityType, string>;

const FALLBACK_ICON = 'user';
type OpportunityIconName =
  | (typeof opportunityTypeConfigs)[OpportunityType]['icon']
  | (typeof demoOpportunityTypeIcons)[keyof typeof demoOpportunityTypeIcons]
  | typeof FALLBACK_ICON;

/**
 * Gets the FontAwesome icon name for an opportunity type
 * First checks the config, falls back to default mappings
 */
export function getOpportunityTypeIcon(type: RealOrDemoOpportunityType): OpportunityIconName {
  if (has(opportunityTypeConfigs, type)) return opportunityTypeConfigs[type].icon;
  if (has(demoOpportunityTypeIcons, type)) return demoOpportunityTypeIcons[type];
  return FALLBACK_ICON;
}

/**
 * Gets all unique icons used across all opportunity types
 */
export function getAllOpportunityTypeIcons(): readonly string[] {
  return R.unique(realAndDemoOpportunityTypes.map(getOpportunityTypeIcon));
}

interface ScoringConfig {
  // Base importance of this opportunity type (0-100)
  baseImportance: number;

  // How quickly the score decays with age (higher = faster decay)
  // Different types will have different decay rates
  ageDecayRate: number;

  // Weight for bill rate importance (0-1)
  // Higher for placement/submission types
  billRateWeight: number;
}

export interface OpportunityTypeGlobalConfig<T extends OpportunityType = OpportunityType> {
  enabled: boolean;

  // Minimum access level required to see this opportunity type.
  // Uses the AccessLevel hierarchy: public < embeddedUser < user < manager < toroAdmin
  // Defaults to 'user' if not specified.
  visibility?: AccessLevel;

  // FontAwesome icon name (from solid set)
  icon: string;

  managerSummaryTemplate: string;
  recruiterSummaryTemplate: string;
  verificationConfig: {
    promptType: OpportunityVerificationPromptType;
    schema: AnyOpportunityDetectionSchema;
    activityDataLookBack: DurationLikeObject;
    confidenceThreshold: number;
  };
  duplicateQueryFields?: readonly string[];
  preventDuplicatesWhenManuallyResolved?: boolean;
  // Support disabling daily LLM re-analysis for black-and-white opportunity types
  skipRevalidation?: boolean;
  // How often to attempt auto-resolution (in hours). Defaults to 24 if not specified.
  // Set to 1 for lightweight auto-resolvers that should run every hour.
  autoResolveThrottleHours?: number;
  // New scoring configuration
  scoringConfig?: ScoringConfig;

  // Analytics configuration
  analytics?: {
    enabled: boolean;
    description: string;
  };

  // Display configuration
  display?: {
    involvesMultipleCandidates?: boolean;
    activityType?: 'standard' | 'responseTimeExamples' | 'none';
  };

  // Default StaffIQ timing configuration (can be overridden per-account)
  baseConfig: OpportunityTypeConfigs[T];

  // Any extra type-specific constants
  extraConstants?: Record<string, unknown>;

  flowConfig?: {
    /** Flow field names that should trigger a cache reset when modified (in addition to 'enabled' which is always checked) */
    resetCacheOnConfigFieldsChange: readonly string[];
  };
}

export const opportunityTypeConfigs = {
  followUpFailure: {
    enabled: true,
    icon: 'person-circle-question',
    managerSummaryTemplate:
      "Recruiter {{recruiter.firstName}} {{recruiter.lastName}} has not responded to candidate {{candidate.firstName}} {{candidate.lastName}}'s question in {{timeSince data.timestamp}}.",
    recruiterSummaryTemplate:
      "Candidate {{candidate.firstName}} {{candidate.lastName}} asked a question {{timeSince data.timestamp}} ago that hasn't been answered.",
    verificationConfig: {
      promptType: 'opportunityVerification_followUpFailure',
      schema: QuestionDetectionResult,
      activityDataLookBack: { weeks: 1 },
      confidenceThreshold: 6,
    },
    duplicateQueryFields: ['candidate'],
    preventDuplicatesWhenManuallyResolved: false,
    skipRevalidation: true,
    autoResolveThrottleHours: 1, // Lightweight resolver, check every hour for a response
    scoringConfig: {
      baseImportance: 60,
      ageDecayRate: 0.3, // Decays quickly
      billRateWeight: 0.0, // Not bill rate dependent
    },
    analytics: {
      enabled: true,
      description:
        'Contains analytics about follow-up failures including inquiry types, response formats needed, and insights',
    },
    baseConfig: {
      escalationTiming: {
        detectAfter: { hours: 48 }, // 2 days
        escalateAfter: { hours: 52 }, // 2 days + 4 hours
        escalationMode: 'recruiter-then-manager',
      },
    },
  },
  placementBackOutRisk: {
    enabled: true,
    icon: 'arrow-right-to-bracket',
    managerSummaryTemplate: `Candidate {{candidate.firstName}} {{candidate.lastName}} at risk of not starting "{{placement.mappedData.jobTitle}}" as scheduled to start on {{toDate placement.startDate}}.`,
    recruiterSummaryTemplate: `Candidate {{candidate.firstName}} {{candidate.lastName}} at risk of not starting "{{placement.mappedData.jobTitle}}" as scheduled to start on {{toDate placement.startDate}}.`,
    extraConstants: {
      // How far forward to check for placements
      placementStartDateLookAhead: { days: 30 },
    },
    verificationConfig: {
      promptType: 'opportunityVerification_placementBackOutRisk',
      schema: PlacementRiskAssessment,
      activityDataLookBack: { weeks: 1 },
      confidenceThreshold: 8,
    },
    // Which fields to check to determine that an existing opportunity is a duplicate
    duplicateQueryFields: ['placement'],
    preventDuplicatesWhenManuallyResolved: true,
    scoringConfig: {
      baseImportance: 75, // Highest importance
      ageDecayRate: 0.05, // Decays slowly
      billRateWeight: 0.8, // Heavily bill rate dependent
    },
    analytics: {
      enabled: true,
      description:
        'Contains analytics about placement risks including risk signals, company distribution, and back-out factors',
    },
    // TODO: detectAfter/escalateAfter not yet supported for placement-based types
    baseConfig: {
      escalationTiming: {
        detectAfter: { hours: 0 },
        escalateAfter: { hours: 0 },
        escalationMode: 'recruiter-then-manager',
      },
    },
  },
  placementReportingFrustration: {
    enabled: true,
    icon: 'briefcase',
    managerSummaryTemplate: `Candidate {{candidate.firstName}} {{candidate.lastName}} has unresolved issues with their current assignment as "{{placement.mappedData.jobTitle}}" that started on {{toDate placement.startDate}}.`,
    recruiterSummaryTemplate: `Candidate {{candidate.firstName}} {{candidate.lastName}} has unresolved issues with their current assignment as "{{placement.mappedData.jobTitle}}" that started on {{toDate placement.startDate}}.`,
    verificationConfig: {
      promptType: 'opportunityVerification_placementFrustration',
      schema: PlacementFrustrationAssessment,
      activityDataLookBack: { days: 45 },
      confidenceThreshold: 8,
    },
    duplicateQueryFields: ['placement'],
    preventDuplicatesWhenManuallyResolved: true,
    scoringConfig: {
      baseImportance: 75,
      ageDecayRate: 0.1, // Decays medium
      billRateWeight: 0.8, // Heavily bill rate dependent
    },
    analytics: {
      enabled: false,
      description:
        'Contains analytics about placement retention risk patterns and unresolved assignment issues',
    },
    // TODO: detectAfter/escalateAfter not yet supported for placement-based types
    baseConfig: {
      escalationTiming: {
        detectAfter: { hours: 0 },
        escalateAfter: { hours: 0 },
        escalationMode: 'recruiter-then-manager',
      },
    },
  },
  leadAbandonment: {
    // Disabled until #987 is done
    enabled: false,
    icon: 'comment-slash',
    managerSummaryTemplate:
      'Recruiter {{recruiter.firstName}} {{recruiter.lastName}} abandoned candidate {{candidate.firstName}} {{candidate.lastName}} after a single outreach attempt {{timeSince data.timestamp}} ago.',
    recruiterSummaryTemplate:
      'You have not followed up with candidate {{candidate.firstName}} {{candidate.lastName}} after a single outreach attempt {{timeSince data.timestamp}} ago.',
    verificationConfig: {
      promptType: 'opportunityVerification_leadAbandonment',
      schema: LeadAbandonmentAssessment,
      activityDataLookBack: { weeks: 1 },
      confidenceThreshold: 7,
    },
    duplicateQueryFields: ['candidate'],
    preventDuplicatesWhenManuallyResolved: true,
    skipRevalidation: true,
    autoResolveThrottleHours: 1, // Lightweight resolver, check every hour for lead engagement
    scoringConfig: {
      baseImportance: 50,
      ageDecayRate: 0.1,
      billRateWeight: 0.3,
    },
    analytics: {
      enabled: false,
      description:
        'Contains analytics about lead abandonment patterns and early-stage engagement failures',
    },
    baseConfig: {
      escalationTiming: {
        detectAfter: { hours: 72 }, // 3 days
        escalateAfter: { hours: 84 }, // 3 days + 12 hours
        escalationMode: 'recruiter-then-manager',
      },
    },
  },
  lossToCompetitor: {
    enabled: true,
    icon: 'handshake-slash',
    managerSummaryTemplate:
      'Candidate {{candidate.firstName}} {{candidate.lastName}} considering a position with {{#if data.competitorName}}{{data.competitorName}}{{else}}another staffing company{{/if}}.',
    recruiterSummaryTemplate:
      'Candidate {{candidate.firstName}} {{candidate.lastName}} considering a position with {{#if data.competitorName}}{{data.competitorName}}{{else}}another staffing company{{/if}}.',
    verificationConfig: {
      promptType: 'opportunityVerification_lossToCompetitor',
      schema: CompetitorLossAssessment,
      activityDataLookBack: { days: 30 },
      confidenceThreshold: 7,
    },
    duplicateQueryFields: ['candidate'],
    preventDuplicatesWhenManuallyResolved: true,
    scoringConfig: {
      baseImportance: 40,
      ageDecayRate: 0.2,
      billRateWeight: 0.4,
    },
    analytics: {
      enabled: true,
      description:
        'Contains analytics about losses to competitors including competitor breakdowns, category distributions, and insights',
    },
    baseConfig: {
      escalationTiming: {
        detectAfter: { hours: 48 }, // 2 days
        escalateAfter: { hours: 52 }, // 2 days + 4 hours
        escalationMode: 'manager-only',
      },
    },
  },
  responseTimeImprovement: {
    enabled: true,
    icon: 'forward-fast',
    managerSummaryTemplate:
      "Recruiter {{recruiter.firstName}} {{recruiter.lastName}}'s response times have improved from {{data.responseTimeMetrics.historicalAverage}} min to {{data.responseTimeMetrics.currentAverage}} min ({{data.responseTimeMetrics.percentageChange}}% faster).",
    recruiterSummaryTemplate:
      'Your response times have improved from {{data.responseTimeMetrics.historicalAverage}} min to {{data.responseTimeMetrics.currentAverage}} min ({{data.responseTimeMetrics.percentageChange}}% faster).',
    verificationConfig: {
      promptType: 'opportunityVerification_responseTimeImprovement',
      schema: ResponseTimeAssessment,
      activityDataLookBack: { weeks: 4 },
      confidenceThreshold: 7,
    },
    duplicateQueryFields: ['recruiter'],
    preventDuplicatesWhenManuallyResolved: false,
    scoringConfig: {
      baseImportance: 20,
      ageDecayRate: 0.15,
      billRateWeight: 0.0,
    },
    analytics: {
      enabled: false,
      description: 'Tracks improvements in recruiter response times to candidate messages',
    },
    display: {
      involvesMultipleCandidates: true,
      activityType: 'responseTimeExamples',
    },
    baseConfig: {
      escalationTiming: {
        detectAfter: { days: 28 },
        escalateAfter: { days: 29 },
        escalationMode: 'manager-only',
      },
    },
  },
  responseTimeDecline: {
    enabled: true,
    icon: 'backward-fast',
    managerSummaryTemplate:
      "Recruiter {{recruiter.firstName}} {{recruiter.lastName}}'s response times have slowed from {{data.responseTimeMetrics.historicalAverage}} min to {{data.responseTimeMetrics.currentAverage}} min ({{data.responseTimeMetrics.percentageChange}}% slower).",
    recruiterSummaryTemplate:
      'Your response times have slowed from {{data.responseTimeMetrics.historicalAverage}} min to {{data.responseTimeMetrics.currentAverage}} min ({{data.responseTimeMetrics.percentageChange}}% slower).',
    verificationConfig: {
      promptType: 'opportunityVerification_responseTimeDecline',
      schema: ResponseTimeAssessment,
      activityDataLookBack: { weeks: 4 },
      confidenceThreshold: 7,
    },
    duplicateQueryFields: ['recruiter'],
    preventDuplicatesWhenManuallyResolved: false,
    scoringConfig: {
      baseImportance: 30,
      ageDecayRate: 0.2,
      billRateWeight: 0.0,
    },
    analytics: {
      enabled: false,
      description: 'Tracks declines in recruiter response times to candidate messages',
    },
    display: {
      involvesMultipleCandidates: true,
      activityType: 'responseTimeExamples',
    },
    baseConfig: {
      escalationTiming: {
        detectAfter: { days: 28 },
        escalateAfter: { days: 29 },
        escalationMode: 'manager-only',
      },
    },
  },
  phoneScreenFollowUp: {
    enabled: true,
    icon: 'user-clock',
    managerSummaryTemplate:
      'Candidate {{candidate.firstName}} {{candidate.lastName}} was screened {{timeSince data.timestamp}} ago but has not been submitted.',
    recruiterSummaryTemplate:
      'Candidate {{candidate.firstName}} {{candidate.lastName}} was screened {{timeSince data.timestamp}} ago but has not been submitted.',
    verificationConfig: {
      promptType: 'opportunityVerification_phoneScreenFollowUp',
      schema: PhoneScreenFollowUpAssessment,
      activityDataLookBack: { days: 14 }, // Look at activity after the phone screen
      confidenceThreshold: 6,
    },
    duplicateQueryFields: ['candidate'],
    preventDuplicatesWhenManuallyResolved: true,
    extraConstants: {
      /** How many business days after phone screen to wait before creating opportunity
       * @deprecated - TODO: replace with escalationTiming.detectHours
       * */
      followUpWindowDays: 14,
      // Maximum outbound activities after phone screen before skipping (indicates active engagement)
      maxOutboundActivitiesAfterScreen: 2,
    },
    scoringConfig: {
      baseImportance: 55, // Medium-high importance
      ageDecayRate: 0.15, // Decays at medium rate
      billRateWeight: 0.2, // Low bill rate dependency
    },
    analytics: {
      enabled: false,
      description:
        'Contains analytics about phone screen follow-ups including time to submission patterns and conversion rates',
    },
    baseConfig: {
      escalationTiming: {
        detectAfter: { days: 14 },
        escalateAfter: { days: 15 },
        escalationMode: 'recruiter-then-manager',
      },
    },
  },
  handoffFailure: {
    enabled: true,
    icon: 'people-arrows',
    managerSummaryTemplate:
      'Handoff "{{data.flowName}}" started {{timeSince data.timestamp}} ago has not been completed.',
    recruiterSummaryTemplate:
      'Handoff "{{data.flowName}}" started {{timeSince data.timestamp}} ago has not been completed.',
    verificationConfig: {
      promptType: 'opportunityVerification_handoffFailure',
      schema: HandoffFailureAssessment,
      activityDataLookBack: { days: 30 },
      confidenceThreshold: 6,
    },
    duplicateQueryFields: ['candidate', 'data.startNoteId'],
    preventDuplicatesWhenManuallyResolved: true,
    skipRevalidation: false,
    scoringConfig: {
      baseImportance: 65, // Medium-high importance
      ageDecayRate: 0.2, // Decays at moderate rate
      billRateWeight: 0.1, // Minimal bill rate dependency
    },
    analytics: {
      enabled: false,
      description: 'Handoff failure analytics',
    },
    flowConfig: {
      resetCacheOnConfigFieldsChange: ['completionWindowDays'],
    },
    baseConfig: {
      escalationTiming: {
        detectAfter: { hours: 0 }, // Immediate after completion window expires
        escalateAfter: { hours: 24 },
        escalationMode: 'recruiter-then-manager',
      },
      flows: [],
    },
  },
  inboundFollowUp: {
    enabled: true,
    visibility: 'toroAdmin',
    icon: 'comment-dots',
    managerSummaryTemplate:
      'Candidate {{candidate.firstName}} {{candidate.lastName}} responded {{timeSince data.timestamp}} ago but has not received follow-up.',
    // TODO: phrase differently for recruiter template if needed
    recruiterSummaryTemplate:
      'Candidate {{candidate.firstName}} {{candidate.lastName}} responded {{timeSince data.timestamp}} ago but has not received follow-up.',
    verificationConfig: {
      promptType: 'opportunityVerification_inboundFollowUp',
      schema: InboundFollowUpAssessment,
      activityDataLookBack: { days: 14 },
      confidenceThreshold: 6,
    },
    duplicateQueryFields: ['candidate', 'data.startNoteId'],
    preventDuplicatesWhenManuallyResolved: true,
    skipRevalidation: false,
    autoResolveThrottleHours: 1,
    scoringConfig: {
      baseImportance: 60,
      ageDecayRate: 0.2,
      billRateWeight: 0.1,
    },
    analytics: {
      enabled: false,
      description: 'Inbound follow-up analytics',
    },
    flowConfig: {
      resetCacheOnConfigFieldsChange: ['startActions', 'inboundActions'],
    },
    baseConfig: {
      escalationTiming: {
        detectAfter: { hours: 24 },
        escalateAfter: { hours: 48 },
        escalationMode: 'manager-only',
      },
      flows: [],
    },
  },
  workersCompLegalRisk: {
    enabled: true,
    icon: 'triangle-exclamation',
    managerSummaryTemplate:
      'Candidate {{candidate.firstName}} {{candidate.lastName}} reported potential workers comp or safety issue.',
    recruiterSummaryTemplate:
      'Candidate {{candidate.firstName}} {{candidate.lastName}} reported potential workers comp or safety issue.',
    verificationConfig: {
      promptType: 'opportunityVerification_workersCompLegalRisk',
      schema: WorkersCompLegalRiskAssessment,
      activityDataLookBack: { days: 7 },
      confidenceThreshold: 7,
    },
    duplicateQueryFields: ['candidate'],
    preventDuplicatesWhenManuallyResolved: false,
    scoringConfig: {
      baseImportance: 100,
      ageDecayRate: 0.01,
      billRateWeight: 0.0,
    },
    analytics: {
      enabled: false,
      description: 'Contains analytics about workers comp and legal risk incidents',
    },
    baseConfig: {
      escalationTiming: {
        detectAfter: { hours: 0 }, // Immediate
        escalateAfter: { hours: 6 },
        escalationMode: 'recruiter-then-manager',
      },
    },
  },
  // } as const satisfies Record<OpportunityType, OpportunityTypeGlobalConfig>;
} as const satisfies {
  [T in OpportunityType]: OpportunityTypeGlobalConfig<T>;
};

const hasConfig = R.isIncludedIn(OpportunityType.options);

export function getOpportunityTypeConfig<T extends OpportunityType>(
  key: T
): OpportunityTypeGlobalConfig<T>;
export function getOpportunityTypeConfig(key: string): OpportunityTypeGlobalConfig | null;
export function getOpportunityTypeConfig(key: string): OpportunityTypeGlobalConfig | null {
  if (hasConfig(key)) {
    return opportunityTypeConfigs[key];
  }
  return null;
}

export function getOpportunityTypeConfigStrict<T extends OpportunityType>(
  key: T
): OpportunityTypeGlobalConfig<T> {
  const config = getOpportunityTypeConfig(key);
  if (!config) {
    throw new Error(`No config for opportunity type ${key}`);
  }
  return config;
}

export const ALL_OPPORTUNITY_TYPE_ICONS: readonly OpportunityIconName[] = [
  ...R.unique(realAndDemoOpportunityTypes.map(getOpportunityTypeIcon)),
] as const;

export type OpportunityParsedResponse<T extends OpportunityType> =
  T extends OpportunityType ?
    z.infer<(typeof opportunityTypeConfigs)[T]['verificationConfig']['schema']>
  : never;

const configsByPromptType = R.pipe(
  opportunityTypeConfigs,
  R.mapValues((config, opportunityType) => ({ config, opportunityType })),
  R.mapKeys((_key, { config }) => config.verificationConfig.promptType)
);

export function getConfigForPromptType(
  promptType: string
): { opportunityType: OpportunityType; config: OpportunityTypeGlobalConfig } | null {
  if (has(configsByPromptType, promptType)) {
    return configsByPromptType[promptType] ?? null;
  } else {
    return null;
  }
}

export const opportunityTypesWithVerification: readonly OpportunityType[] =
  realOpportunityTypes.filter((key) => opportunityTypeConfigs[key]?.verificationConfig);
export const enabledOpportunityTypes: readonly OpportunityType[] = realOpportunityTypes.filter(
  (key) => opportunityTypeConfigs[key].enabled
);

export const EnabledOpportunityType = z.enum(enabledOpportunityTypes);
export type EnabledOpportunityType = z.infer<typeof EnabledOpportunityType>;

export const ConfigurableOpportunityType = OpportunityType;
export type ConfigurableOpportunityType = OpportunityType;

const flowBasedOpportunityTypes = R.pipe(
  opportunityTypeConfigs,
  R.pickBy((config) => 'flowConfig' in config),
  R.pickBy((config) => R.isTruthy(config.flowConfig)),
  R.keys(),
  (list) => Object.freeze(list)
);

/**
 * Opportunity types that have flow-based configuration
 * */
export type FlowBasedOpportunityType = (typeof flowBasedOpportunityTypes)[number];
export const isFlowBasedOpportunityType = R.isIncludedIn(flowBasedOpportunityTypes);

const responseTimeOpportunityTypes = R.pipe(
  opportunityTypeConfigs,
  R.pickBy((config) => 'display' in config),
  R.pickBy((config) => config.display?.activityType === 'responseTimeExamples'),
  R.keys()
);

export const isResponseTimeOpportunityType = R.isIncludedIn(responseTimeOpportunityTypes);
export type ResponseTimeOpportunityType = (typeof responseTimeOpportunityTypes)[number];
export function isResponseTimeOpportunity<T extends { type: RealOrDemoOpportunityType }>(
  opportunity: T
): opportunity is T & { type: ResponseTimeOpportunityType } {
  return isResponseTimeOpportunityType(opportunity.type);
}

// Default visibility for opportunity types that don't specify one
const DEFAULT_VISIBILITY = 'user' as const satisfies AccessLevel;

/**
 * Returns opportunity types visible to a user with the given role.
 * Uses the AccessLevel hierarchy to filter types based on their visibility setting.
 * @param userRole - The user's role for visibility filtering
 * @param includeDisabled - If true, includes all types (for demo mode); otherwise only enabled types
 */
export function getVisibleOpportunityTypes(
  userRole: AccessLevel,
  includeDisabled?: false
): readonly OpportunityType[];
export function getVisibleOpportunityTypes(
  userRole: AccessLevel,
  includeDisabled: boolean
): readonly (OpportunityType | DemoOpportunityType)[];
export function getVisibleOpportunityTypes(
  userRole: AccessLevel,
  includeDisabled = false
): readonly (OpportunityType | DemoOpportunityType)[] {
  const types = includeDisabled ? realAndDemoOpportunityTypes : enabledOpportunityTypes;
  return types.filter((key) => {
    const config = getOpportunityTypeConfig(key);
    const visibility = config?.visibility ?? DEFAULT_VISIBILITY;
    return doesUserHaveAccess(visibility, userRole ?? DEFAULT_VISIBILITY);
  });
}

// Re-export for convenience
export { AccessLevel, doesUserHaveAccess } from './permissions.js';

// Tag types that are references to documents we have saved in our database
export const entityTagTypes = [
  'Candidate',
  'Recruiter',
  'Placement',
  'Position',
  'Submission',
  'Manager',
  // Company/ClientCorporation and Team don't have models currently
] as const;

// Other tag types, without associated database entity references
export const nonEntityTagTypes = ['Company', 'Team'] as const;

export const opportunityTagTypes = [...entityTagTypes, ...nonEntityTagTypes] as const;

export type TagType = (typeof opportunityTagTypes)[number];
export type EntityTagType = (typeof entityTagTypes)[number];
export type NonEntityTagType = (typeof nonEntityTagTypes)[number];

const TagTypeEnum = z.enum(opportunityTagTypes);

export { OpportunityStatus };
const closedOpportunityStatuses = [
  'expired',
  'invalid',
  'complete',
] as const satisfies OpportunityStatus[];
export const isClosedOpportunityStatus = R.isIncludedIn(closedOpportunityStatuses);

export const OpportunityScoringDetails = z.object({
  opportunityType: OpportunityType,
  baseImportance: z.number(),
  creationDays: z.number(),
  ageDecay: z.number(),
  submissionDays: z.number().or(z.literal(Infinity)).nullish(),
  placementDays: z.number().or(z.literal(Infinity)).nullish(),
  mostRecentActivityDays: z.number(),
  recencyBonus: z.number(),
  billRate: z.number(),
  billRateBonus: z.number(),
  confidence: z.number().optional(),
  randomMultiplier: z.number().optional(),
  confidencePenalty: z.number().optional(),
  employeeTenureDays: z.number().optional(),
  tenureDeprioritization: z.number().optional(),
  // Response time specific scoring factors
  responseTimeDifferenceMinutes: z.number().optional(),
  responseTimeImpact: z.number().optional(),
  finalScore: z.number(),
});
export type OpportunityScoringDetails = z.infer<typeof OpportunityScoringDetails>;

/**
 * Human-readable labels for opportunity types
 */
export const opportunityTypeTooltips: Record<string, string> = {
  placementBackOutRisk: 'Placement Back-Out Risk',
  placementGhostingRisk: 'Placement Ghosting Risk',
  submissionBackOutRisk: 'Submission Back-Out Risk',
  submissionGhostingRisk: 'Submission Ghosting Risk',
  callOffEscalation: 'Call-Off Escalation',
  facilityCanceledShift: 'Facility Canceled Shift',
  followUpFailure: 'Follow-Up Failure',
  redeploymentRisk: 'Redeployment Risk',
  redeploymentRiskNotEngaged: 'Redeployment Risk - Not Engaged',
  outreachResponseTrendUp: 'Outreach Response Trend Up',
  outreachResponseTrendDown: 'Outreach Response Trend Down',
  engagementAttemptTrendUp: 'Engagement Attempt Trend Up',
  engagementAttemptTrendDown: 'Engagement Attempt Trend Down',
  placementReportingFrustration: 'Placement Retention Risk',
  leadAbandonment: 'Lead Abandonment',
  lossToCompetitor: 'Loss To Competitor',
  responseTimeImprovement: 'Response Time Improvement',
  responseTimeDecline: 'Response Time Decline',
  phoneScreenFollowUp: 'Candidate Screen Follow-Up',
  workersCompLegalRisk: 'Workers Comp / Legal Risk',
  handoffFailure: 'Handoff Failure',
  inboundFollowUp: 'Inbound Follow-Up',
} as const satisfies Record<OpportunityType, string> & Partial<Record<DemoOpportunityType, string>>;

/**
 * Label overrides for owner/recruiter alert emails.
 * Falls back to opportunityTypeTooltips if not specified.
 */
export const opportunityTypeOwnerAlertTooltips = {
  followUpFailure: 'Follow-Up Reminder',
  handoffFailure: 'Handoff Follow-Up',
  leadAbandonment: 'Lead Reengagement',
  inboundFollowUp: 'Inbound Response Follow-Up',
} as const satisfies Partial<Record<OpportunityType, string>>;

// Generate icon paths dynamically from the opportunity type configs
export const opportunityTypeIconPaths: Readonly<Record<OpportunityType, string>> = R.fromKeys(
  realAndDemoOpportunityTypes,
  (type) => `/images/${getOpportunityTypeIcon(type)}-solid-full-24.png`
);

/**
 * Returns default timing configs for all opportunity types that have them.
 * Used by the backend to merge with stored configs in API responses.
 *
 * Note: The frontend has a similar function in staffiq/constants.ts that includes
 * UI metadata (titles, descriptions, templates). This backend version returns
 * only the timing defaults needed for the API response merge.
 */
export function getDefaultOpportunityTypeConfigs(): OpportunityTypeConfigs {
  return R.pipe(
    opportunityTypeConfigs,
    R.mapValues(R.prop('baseConfig')),
    R.pickBy(R.isTruthy),
    // Clone the config so the original won't be mutated
    R.clone()
  );
}
